import {
  ArrowRight,
  Lightbulb,
  Sparkles,
  Target,
  TrendingUp,
  Users,
} from "lucide-react";

import { Button } from "@/components/ui/button";

interface GrowWelcomeProps {
  onGetStarted: () => void;
  hasConnectedAccounts: boolean;
}

export function GrowWelcome({
  onGetStarted,
  hasConnectedAccounts,
}: GrowWelcomeProps) {
  const features = [
    {
      icon: (
        <TrendingUp className="h-6 w-6 text-green-600 dark:text-green-400" />
      ),
      title: "Creator Mind Map",
      description:
        "Analyze your content style, audience insights, and top-performing topics automatically.",
      gradient: "from-green-500/10 to-emerald-500/10",
      iconBg:
        "bg-gradient-to-br from-green-100 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30",
      border: "border-green-200/50 dark:border-green-800/50",
    },
    {
      icon: (
        <Lightbulb className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
      ),
      title: "Smart Hook Generator",
      description:
        "Generate personalized content hooks and outlines based on your unique style and audience.",
      gradient: "from-yellow-500/10 to-orange-500/10",
      iconBg:
        "bg-gradient-to-br from-yellow-100 to-orange-100 dark:from-yellow-900/30 dark:to-orange-900/30",
      border: "border-yellow-200/50 dark:border-yellow-800/50",
    },
    {
      icon: <Target className="h-6 w-6 text-blue-600 dark:text-blue-400" />,
      title: "Content Lifecycle",
      description:
        "Track your content journey with a Kanban board for ideas, script outlines, filming, and analytics.",
      gradient: "from-blue-500/10 to-purple-500/10",
      iconBg:
        "bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30",
      border: "border-blue-200/50 dark:border-blue-800/50",
    },
    {
      icon: <Users className="h-6 w-6 text-purple-600 dark:text-purple-400" />,
      title: "Brand Opportunities",
      description:
        "Connect with brands looking for creators in your niche and monetize your content.",
      gradient: "from-purple-500/10 to-pink-500/10",
      iconBg:
        "bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30",
      border: "border-purple-200/50 dark:border-purple-800/50",
    },
  ];

  return (
    <div className="flex h-full w-full bg-gradient-to-br from-slate-50 via-white to-slate-100 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 px-6 py-8">
      <div className="z-10 mx-auto flex h-full w-full max-w-screen-xl flex-col">
        {/* Header Section */}
        <div className="mb-16 text-center">
          <div className="mb-6">
            <div className="relative inline-block mb-4">
              <h1 className="text-5xl font-bold ">Grow Studio</h1>
              <Sparkles className="absolute -top-2 -right-8 h-6 w-6 text-yellow-400 animate-pulse" />
            </div>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Generate personalized hooks, outlines, and content ideas directly
              from your own content style and audience data.
            </p>
          </div>

          {!hasConnectedAccounts && (
            <div className="mb-8 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-700 rounded-xl max-w-md mx-auto shadow-sm">
              <p className="text-sm text-blue-700 dark:text-blue-300 font-medium">
                🔗 Connect your TikTok or Instagram to generate personalized
                content ideas
              </p>
            </div>
          )}
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20">
          {features.map((feature, index) => (
            <div
              key={index}
              className={`relative rounded-2xl border ${feature.border} bg-gradient-to-br ${feature.gradient} backdrop-blur-sm p-8 hover:shadow-lg hover:shadow-black/5 dark:hover:shadow-black/20 transition-all duration-300 hover:-translate-y-1 group`}
            >
              <div className="flex items-start gap-6">
                <div
                  className={`flex-shrink-0 p-4 ${feature.iconBg} rounded-2xl shadow-sm group-hover:shadow-md transition-shadow duration-300`}
                >
                  {feature.icon}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* How It Works */}
        <div className="mb-20">
          <h2 className="text-3xl font-bold text-center mb-12 bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
            How It Works
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center group">
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-2xl flex items-center justify-center mx-auto mb-6 text-xl font-bold shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-105">
                  1
                </div>
                <div className="absolute inset-0 w-16 h-16 bg-gradient-to-br from-blue-400 to-blue-500 rounded-2xl mx-auto blur-xl opacity-30 group-hover:opacity-50 transition-opacity duration-300"></div>
              </div>
              <h3 className="font-bold text-gray-900 dark:text-white mb-3 text-lg">
                Connect TikTok/IG/YT
              </h3>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                Link your accounts to analyze your content style and audience
              </p>
            </div>
            <div className="text-center group">
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-2xl flex items-center justify-center mx-auto mb-6 text-xl font-bold shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-105">
                  2
                </div>
                <div className="absolute inset-0 w-16 h-16 bg-gradient-to-br from-purple-400 to-purple-500 rounded-2xl mx-auto blur-xl opacity-30 group-hover:opacity-50 transition-opacity duration-300"></div>
              </div>
              <h3 className="font-bold text-gray-900 dark:text-white mb-3 text-lg">
                Generate Mind Map
              </h3>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                Automatically create your Creator Mind Map with insights and top
                topics
              </p>
            </div>
            <div className="text-center group">
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 text-white rounded-2xl flex items-center justify-center mx-auto mb-6 text-xl font-bold shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-105">
                  3
                </div>
                <div className="absolute inset-0 w-16 h-16 bg-gradient-to-br from-green-400 to-green-500 rounded-2xl mx-auto blur-xl opacity-30 group-hover:opacity-50 transition-opacity duration-300"></div>
              </div>
              <h3 className="font-bold text-gray-900 dark:text-white mb-3 text-lg">
                Get Hook Ideas
              </h3>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                Access personalized hooks and outlines in Content Producer
              </p>
            </div>
            <div className="text-center group">
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 text-white rounded-2xl flex items-center justify-center mx-auto mb-6 text-xl font-bold shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-105">
                  4
                </div>
                <div className="absolute inset-0 w-16 h-16 bg-gradient-to-br from-orange-400 to-orange-500 rounded-2xl mx-auto blur-xl opacity-30 group-hover:opacity-50 transition-opacity duration-300"></div>
              </div>
              <h3 className="font-bold text-gray-900 dark:text-white mb-3 text-lg">
                Monetize Content
              </h3>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                Find brand partnerships through CreatorGrid marketplace
              </p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center">
          <div className="relative rounded-3xl border border-gray-200/50 dark:border-gray-700/50 bg-gradient-to-br from-white via-gray-50 to-white dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-12 shadow-xl">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 via-purple-500/5 to-pink-500/5 rounded-3xl"></div>
            <div className="relative z-10">
              <h2 className="text-3xl font-bold mb-6 ">
                Ready to Generate Content Ideas?
              </h2>
              <p className="text-gray-600 dark:text-gray-300 mb-8 max-w-2xl mx-auto text-lg leading-relaxed">
                Create your Creator Mind Map and start generating personalized
                hooks and content outlines tailored to your unique style.
              </p>
              <Button
                onClick={onGetStarted}
                size="lg"
                disabled={!hasConnectedAccounts}
                className="px-10 py-4 text-lg font-semibold border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
              >
                {hasConnectedAccounts
                  ? "✨ Start Creating Content"
                  : "🔗 Connect Accounts First"}
                <ArrowRight className="ml-3 h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
